// frontend/src/components/navbar/useRowFit.js
//
// Does the app navbar's full row fit in its pill? A FIT-BASED switch between
// the inline row and the burger, in place of fixed width breakpoints: the row
// an admin, a BI + interventions customer, a long name or the English labels
// produce differs by hundreds of px, so no single width was right for all of
// them (review R4 #1: the 8-link admin row ran under the account pill at
// 1200-1350px, while a customer's row was folded at 900-1080px though it fit).
//
// HOW IT MEASURES. Navbar keeps the row's links and its desktop actions in the
// DOM in both shapes - in the burger shape as an invisible, inert,
// out-of-flow copy (`lf-app-nav-measure`, appNav.css) - so their natural
// widths are readable at every width. The row needs:
//
//   brand + links + desktop actions + 2 x NAV_ROW_MIN_AIR_PX
//
// The links row is centred by auto margins, so the spare width splits evenly;
// 2 x 16px of it guarantees >= 16px between the brand lockup and the first
// link and between the last link and the account pill. Available = the pill's
// content box. Everything is read off the live DOM: no px number is copied
// from the CSS.
//
// HYSTERESIS. Shrinking folds the row the moment it no longer fits; growing
// back needs NAV_ROW_FIT_HYSTERESIS_PX more, so a width hovering at the edge
// (a scrollbar appearing, a sub-pixel font metric) cannot flicker the bar.
//
// WHEN IT CANNOT MEASURE (no ResizeObserver - SSR, the prerender snapshot,
// jsdom) it returns null and Navbar keeps its first-render guess, the width
// breakpoints of navTokens. In a browser the first measurement runs in a
// layout effect, before the first paint, so the guess is never seen.

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

import { NAV_ROW_FIT_HYSTERESIS_PX, NAV_ROW_MIN_AIR_PX } from "./navTokens";

/** True where the browser can tell us when the bar's boxes change size. */
export const canMeasureRow = () =>
  typeof window !== "undefined" && typeof window.ResizeObserver === "function";

/**
 * The decision itself, a pure function so it is testable without a layout.
 *
 * @param {object} m
 * @param {number} m.available the pill's content-box width, px.
 * @param {number} m.needed    the row's width at its minimum air, px.
 * @param {boolean} m.wasRow   whether the row is showing now (hysteresis).
 * @returns {boolean|null} true = the row fits, null = nothing measurable yet.
 */
export function decideRowFits({ available, needed, wasRow }) {
  if (!(available > 0) || !(needed > 0)) return null;
  return wasRow ? available >= needed : available >= needed + NAV_ROW_FIT_HYSTERESIS_PX;
}

/** An element's rendered width, fractional. */
const widthOf = (el) => (el ? el.getBoundingClientRect().width : 0);

/**
 * The links' natural width: each item's own width plus the row's gaps. Summed
 * from the children rather than read off the container, because in the row
 * shape the container is a flex item that may already be squeezed.
 */
const naturalLinksWidth = (links) => {
  if (!links) return 0;
  const items = Array.from(links.children);
  if (!items.length) return 0;
  const gap = parseFloat(window.getComputedStyle(links).columnGap) || 0;
  return items.reduce((sum, el) => sum + widthOf(el), 0) + gap * (items.length - 1);
};

/** The pill's content box: its inner width minus its own inline padding. */
const contentWidth = (inner) => {
  if (!inner) return 0;
  const cs = window.getComputedStyle(inner);
  return inner.clientWidth - (parseFloat(cs.paddingLeft) || 0) - (parseFloat(cs.paddingRight) || 0);
};

/**
 * @param {object} p
 * @param {boolean} p.enabled  measure at all (the signed-in row only).
 * @param {boolean} p.initialRow the first-render guess: is the row showing?
 * @param {{current: HTMLElement|null}} p.innerRef   the pill (`nav`).
 * @param {{current: HTMLElement|null}} p.brandRef   the brand group (the lockup).
 * @param {{current: HTMLElement|null}} p.linksRef   the links row.
 * @param {{current: HTMLElement|null}} p.actionsRef the desktop actions group.
 * @param {string} p.contentKey changes whenever the row's CONTENT changes
 *   (language, items, name, upgrade label), so it is re-measured even when no
 *   box is resized by it.
 * @returns {boolean|null} whether the row fits, or null before a measurement.
 */
export default function useRowFit({ enabled, initialRow, innerRef, brandRef, linksRef, actionsRef, contentKey }) {
  const [fits, setFits] = useState(null);
  const fitsRef = useRef(null);
  const initialRowRef = useRef(initialRow);
  initialRowRef.current = initialRow;

  const measure = useCallback(() => {
    if (!enabled) return;
    const inner = innerRef.current;
    const links = linksRef.current;
    if (!inner || !links) return;
    const needed =
      widthOf(brandRef.current) +
      naturalLinksWidth(links) +
      widthOf(actionsRef.current) +
      2 * NAV_ROW_MIN_AIR_PX;
    const wasRow = fitsRef.current === null ? initialRowRef.current : fitsRef.current;
    const next = decideRowFits({ available: contentWidth(inner), needed, wasRow });
    if (next === null || next === fitsRef.current) return;
    fitsRef.current = next;
    setFits(next);
  }, [enabled, innerRef, brandRef, linksRef, actionsRef]);

  // Before the first paint, and again whenever what the row carries changes.
  useLayoutEffect(() => {
    if (enabled && canMeasureRow()) measure();
  }, [enabled, measure, contentKey]);

  // A resize of the pill (the viewport) or of any measured group (a web font
  // arriving, the account name loading) re-measures. The callback only reads
  // and sets state - React commits in a later task - so it cannot start a
  // ResizeObserver loop.
  useEffect(() => {
    if (!enabled || !canMeasureRow()) return undefined;
    const observer = new window.ResizeObserver(() => measure());
    [innerRef, brandRef, linksRef, actionsRef].forEach((ref) => {
      if (ref.current) observer.observe(ref.current);
    });
    let cancelled = false;
    // Text widths change when Heebo finishes loading; the pill's own box may
    // not, so wait for the fonts explicitly as well.
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => {
        if (!cancelled) measure();
      }, () => {});
    }
    return () => {
      cancelled = true;
      observer.disconnect();
    };
  // `contentKey` too: a content change may swap the observed elements.
  }, [enabled, measure, innerRef, brandRef, linksRef, actionsRef, contentKey]);

  return enabled ? fits : null;
}
